import { useEffect, useMemo, useState } from 'react';
import { get, patch, post, qs } from '../lib/api';
import { storageGet, storageSet, useDebounced, useLoad } from '../lib/hooks';
import { useSession } from '../lib/session';
import { formatCents } from '../../shared/money';
import { formatDateTime, isValidImei } from '../../shared/format';
import { Badge, Button, Empty, Field, MoneyInput, Sheet, Spinner, enterToNextBox, useAction, useToast } from '../ui/kit';
import { Icon } from '../ui/icons';
import { DeviceArt, ProductPicture } from '../ui/art';
import { DevicePhoto } from '../ui/photo';

// The sale menu: Apple, Samsung, Google, Moto, Gaming, WiFi, Accessories, then each one's groups
// (iPhone, iPad, MacBook...), then the models with their pictures and prices.

export interface MenuItem {
  id: string;
  name: string;
  brand: string | null;
  category_id: string;
  model: string;
  variant: string | null;
  group: string | null;
  price_cents: number;
  taxable: boolean;
  image_url: string | null;
  qty: number;
  price_source: string | null;
  details: { condition?: string; colors?: string[]; fits?: string; note?: string } | null;
}

interface MenuInfo {
  id: string;
  name: string;
  brand: string | null;
  cover: string | null;
  count: number;
  groups: { id: string; name: string; count: number }[];
}

interface Model {
  key: string;
  model: string;
  group: string | null;
  items: MenuItem[];
}

/** Menus where each model is a phone, tablet, laptop or console: tapping it asks for the size, color and IMEI or serial. */
const DEVICE_MENUS = new Set(['apple', 'samsung', 'google', 'moto', 'gaming']);
const NAV_KEY = 'storeos.saleMenu';
const normModel = (s: string) => s.toLowerCase().replace(/\b(5g|lte|4g)\b/g, '').replace(/\bplus\b/g, '+').replace(/[^a-z0-9+]+/g, '');

function groupModels(items: MenuItem[]): Model[] {
  const out: Model[] = [];
  const byKey = new Map<string, Model>();
  for (const it of items) {
    const key = `${it.category_id}|${it.model}`;
    let m = byKey.get(key);
    if (!m) {
      m = { key, model: it.model, group: it.group, items: [] };
      byKey.set(key, m);
      out.push(m);
    }
    m.items.push(it);
  }
  return out;
}

function fromPrice(items: MenuItem[]): number | null {
  const priced = items.filter((i) => i.price_cents > 0).map((i) => i.price_cents);
  return priced.length ? Math.min(...priced) : null;
}

function Picture({ item, size = 92 }: { item: Pick<MenuItem, 'image_url' | 'brand' | 'model' | 'category_id' | 'name'>; size?: number }) {
  const [broken, setBroken] = useState(false);
  if (item.image_url && !broken) {
    return <img className="pic" src={item.image_url} alt={item.name} width={size} height={size} loading="lazy" decoding="async" onError={() => setBroken(true)} />;
  }
  const category = /ipad|tablet/.test(item.category_id) ? 'tablets' : /macbook/.test(item.category_id) ? 'macbooks' : /gaming/.test(item.category_id) ? 'consoles' : null;
  return <DeviceArt brand={item.brand} model={item.model} category={category} size={size} />;
}

/** What adding a phone did: true it's in the cart, "held" the Sell it now question is up, false a message said why not. */
type AddDevice = (d: any) => boolean | 'held';

export function SaleMenu({ q, onAddProduct, onAddDevice, onCustomItem, inCart }: {
  q: string;
  onAddProduct: (p: MenuItem, opts: { note?: string | null; priceCents?: number }) => void;
  onAddDevice: AddDevice;
  /** Sell something once without adding it to inventory (a custom item), named what was searched for. */
  onCustomItem?: (name: string) => void;
  inCart: (id: string) => boolean;
}) {
  const { can } = useSession();
  const [nav, setNav] = useState<{ menu: string | null; group: string | null }>(() => storageGet(NAV_KEY, { menu: null, group: null }));
  const [section, setSection] = useState('');
  const [open, setOpen] = useState<{ model: Model; menu: string } | null>(null);
  const [pricing, setPricing] = useState<{ item: MenuItem; mode: 'sell' | 'edit' } | null>(null);
  const [adding, setAdding] = useState(false);
  useEffect(() => storageSet(NAV_KEY, nav), [nav]);
  useEffect(() => setSection(''), [nav.group]);

  const menus = useLoad<{ menus: MenuInfo[] }>(() => get('/api/sale-menu'), []);
  const menu = menus.data?.menus.find((m) => m.id === nav.menu) ?? null;
  const group = menu?.groups.find((g) => g.id === nav.group) ?? menu?.groups[0] ?? null;
  const dq = useDebounced(q.trim(), 250);
  const items = useLoad<MenuItem[]>(
    () => (dq.length >= 2 ? get(`/api/sale-menu/items${qs({ q: dq, limit: 300 })}`) : group ? get(`/api/sale-menu/items${qs({ category: group.id })}`) : Promise.resolve([])),
    [dq.length >= 2 ? dq : '', group?.id ?? ''],
  );
  const facets = useLoad<any>(() => get('/api/devices/facets'), []);
  const inStock = (brand: string | null, model: string) => {
    const list: { value: string; count: number }[] = brand ? facets.data?.models?.[brand] ?? [] : [];
    const n = normModel(model);
    return list.filter((m) => normModel(m.value) === n).reduce((s, m) => s + m.count, 0);
  };

  const models = useMemo(() => groupModels(items.data ?? []), [items.data]);
  const sections = useMemo(() => [...new Set(models.map((m) => m.group ?? ''))], [models]);
  const shown = section ? models.filter((m) => (m.group ?? '') === section) : models;

  const menuIdOf = (it: MenuItem) => menus.data?.menus.find((m) => m.groups.some((g) => g.id === it.category_id))?.id ?? '';
  const tap = (m: Model) => {
    const first = m.items[0];
    const menuId = menuIdOf(first);
    if (DEVICE_MENUS.has(menuId) || m.items.length > 1) return setOpen({ model: m, menu: menuId });
    if (first.price_cents <= 0) return setPricing({ item: first, mode: 'sell' });
    onAddProduct(first, {});
  };
  const updateItem = (id: string, price: number) => items.setData((list) => (list ?? []).map((i) => (i.id === id ? { ...i, price_cents: price } : i)));

  const searching = dq.length >= 2;

  if (!searching && !menu) {
    return (
      <div className="stack">
        {menus.loading && !menus.data ? <Spinner label="Loading the menu" /> : null}
        <div className="brand-grid" role="list" aria-label="Sale menu">
          {(menus.data?.menus ?? []).map((m) => (
            <button key={m.id} type="button" role="listitem" className={`brand-tile brand-${m.id}`} onClick={() => setNav({ menu: m.id, group: m.groups[0]?.id ?? null })} aria-label={`${m.name}, ${m.count} items`}>
              <span className="brand-pic">{m.cover ? <img src={m.cover} alt="" loading="lazy" decoding="async" onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }} /> : <Icon.bag />}</span>
              <span className="brand-name">{m.name}</span>
              <span className="brand-sub">{m.groups.map((g) => g.name).slice(0, 4).join(' · ') || 'Nothing here yet'}</span>
            </button>
          ))}
        </div>
        {menus.error ? <Empty icon="alert" title="The menu couldn't be loaded.">{menus.error.message}</Empty> : null}
      </div>
    );
  }

  return (
    <div className="stack">
      {searching ? (
        <div className="menu-crumbs"><span className="strong">Menu results for “{dq}”</span></div>
      ) : (
        <div className="menu-crumbs">
          <button type="button" className="crumb-back" onClick={() => setNav({ menu: null, group: null })}><Icon.chevronLeft /> Menu</button>
          <span className="crumb-title grow">{menu?.name}</span>
          {group?.id === 'apple_iphone' && can('inventory.edit') ? <WebsitePricesButton onDone={items.reload} /> : null}
        </div>
      )}
      {!searching && menu && menu.groups.length > 1 ? (
        <div className="chips" role="tablist" aria-label={`${menu.name} groups`}>
          {menu.groups.map((g) => (
            <button key={g.id} type="button" role="tab" className="chip chip-lg" aria-selected={group?.id === g.id} aria-pressed={group?.id === g.id} onClick={() => setNav({ menu: menu.id, group: g.id })}>
              {g.name} <span className={group?.id === g.id ? '' : 'muted'}>{g.count}</span>
            </button>
          ))}
        </div>
      ) : null}
      {!searching && sections.length > 1 ? (
        sections.length <= 12 ? (
          <div className="chips" aria-label="Series">
            <button type="button" className="chip" aria-pressed={section === ''} onClick={() => setSection('')}>All</button>
            {sections.map((sct) => <button key={sct} type="button" className="chip" aria-pressed={section === sct} onClick={() => setSection(sct)}>{sct || 'Other'}</button>)}
          </div>
        ) : (
          <label className="row" style={{ gap: 10 }}>
            <span className="small strong">Show</span>
            <select className="select" style={{ maxWidth: 320 }} value={section} onChange={(e) => setSection(e.target.value)} aria-label="Fits">
              <option value="">All ({models.length})</option>
              {sections.map((sct) => <option key={sct} value={sct}>{sct || 'Other'} ({models.filter((m) => (m.group ?? '') === sct).length})</option>)}
            </select>
          </label>
        )
      ) : null}
      {items.loading && !items.data ? <Spinner /> : null}
      {items.data && !shown.length ? (
        <Empty icon="search" title={searching ? 'Nothing on the menu matches.' : 'Nothing here yet.'}>
          {searching ? 'Try fewer words, or the model number.' : 'Add products to this group in Inventory.'}
          {searching ? (
            <div className="row-wrap" style={{ marginTop: 12, justifyContent: 'center' }}>
              {can('inventory.edit') ? <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>Add “{dq}” as a new item</Button> : null}
              {/* Selling it once doesn't need a manager to add it to inventory first (staff get a PIN at checkout). */}
              {onCustomItem ? <Button variant={can('inventory.edit') ? 'secondary' : 'primary'} icon="tag" onClick={() => onCustomItem(dq)}>Sell “{dq}” as a custom item</Button> : null}
            </div>
          ) : null}
        </Empty>
      ) : null}
      {searching && shown.length > 0 ? (
        <div className="row-wrap">
          {can('inventory.edit') ? <Button size="sm" variant="ghost" icon="plus" onClick={() => setAdding(true)}>Not here? Add a new item</Button> : null}
          {onCustomItem ? <Button size="sm" variant="ghost" icon="tag" onClick={() => onCustomItem(dq)}>{can('inventory.edit') ? 'Sell it once as a custom item' : 'Not here? Sell it as a custom item'}</Button> : null}
        </div>
      ) : null}
      {adding ? <QuickAddSheet name={dq} onClose={() => setAdding(false)} onAdded={(p) => { setAdding(false); onAddProduct(p, {}); items.reload(); }} /> : null}
      <MenuGrid
        models={shown}
        sections={!searching && !section && sections.length > 1}
        tap={tap}
        canEdit={can('inventory.edit')}
        isDeviceMenu={(m) => DEVICE_MENUS.has(menuIdOf(m.items[0]))}
        edit={(it) => setPricing({ item: it, mode: 'edit' })}
        inStock={(m) => (DEVICE_MENUS.has(menuIdOf(m.items[0])) ? inStock(m.items[0].brand, m.model) : m.items.reduce((s, i) => s + Math.max(0, i.qty), 0))}
        inCart={(m) => m.items.some((i) => inCart(i.id))}
      />
      {open ? (
        <ModelSheet
          model={open.model}
          onClose={() => setOpen(null)}
          onAddProduct={(it, opts) => { onAddProduct(it, opts); setOpen(null); }}
          onAddDevice={(d) => {
            const r = onAddDevice(d);
            if (r) setOpen(null);
            return r;
          }}
          onPriceSaved={updateItem}
        />
      ) : null}
      {pricing ? (
        <PriceSheet
          item={pricing.item}
          mode={pricing.mode}
          onClose={() => setPricing(null)}
          onSaved={(price) => updateItem(pricing.item.id, price)}
          onAdd={(it, opts) => { onAddProduct(it, opts); setPricing(null); }}
        />
      ) : null}
    </div>
  );
}

function MenuGrid({ models, sections, tap, canEdit, isDeviceMenu, edit, inStock, inCart }: {
  models: Model[];
  sections: boolean;
  tap: (m: Model) => void;
  canEdit: boolean;
  isDeviceMenu: (m: Model) => boolean;
  edit: (it: MenuItem) => void;
  inStock: (m: Model) => number;
  inCart: (m: Model) => boolean;
}) {
  // Long lists render in pages so a tablet stays quick.
  const [limit, setLimit] = useState(120);
  useEffect(() => setLimit(120), [models]);
  const visible = models.slice(0, limit);
  const blocks: { title: string | null; models: Model[] }[] = [];
  for (const m of visible) {
    const title = sections ? m.group ?? 'Other' : null;
    const last = blocks[blocks.length - 1];
    if (last && last.title === title) last.models.push(m);
    else blocks.push({ title, models: [m] });
  }
  return (
    <>
      {blocks.map((b, i) => (
        <section key={`${b.title}-${i}`} className="menu-section" aria-label={b.title ?? undefined}>
          {b.title ? <h3 className="menu-section-title">{b.title}</h3> : null}
          <div className="items">
            {b.models.map((m) => {
              const first = m.items[0];
              const device = isDeviceMenu(m);
              const from = fromPrice(m.items);
              const sizes = m.items.filter((x) => x.variant).length;
              const stock = inStock(m);
              const sub = device
                ? [sizes > 1 ? `${sizes} sizes` : first.variant, first.details?.condition].filter(Boolean).join(' · ')
                : [first.details?.fits, first.details?.colors?.length ? `${first.details.colors.length} colors` : null, first.details?.note].filter(Boolean).join(' · ');
              return (
                <div key={m.key} className="menu-item-wrap">
                  <button type="button" className="item menu-item" onClick={() => tap(m)} aria-label={`${first.brand && !m.model.startsWith(first.brand) ? `${first.brand} ` : ''}${m.model}${from ? `, ${device && sizes > 1 ? 'from ' : ''}${formatCents(from)}` : ', no price yet'}`}>
                    <span className="pic-wrap"><Picture item={{ ...first, model: m.model }} /></span>
                    <span className="t">{m.model}</span>
                    {sub ? <span className="s">{sub}</span> : null}
                    <span className="p">
                      {from === null ? <span className="ask">Set price</span> : <span className="num">{device && m.items.length > 1 ? <small>from </small> : null}{formatCents(from)}</span>}
                      {inCart(m) ? <Badge tone="blue" icon="check">In cart</Badge> : stock > 0 ? <Badge tone="green">{stock} in stock</Badge> : null}
                    </span>
                  </button>
                  {canEdit && !device && m.items.length === 1 ? (
                    <button type="button" className="menu-edit" aria-label={`Change the price of ${m.model}`} title="Change the price" onClick={() => edit(first)}><Icon.edit /></button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </section>
      ))}
      {models.length > limit ? <Button block onClick={() => setLimit(limit + 240)}>Show more ({models.length - limit} more)</Button> : null}
    </>
  );
}

/** iPhone prices follow westcoastwirelessca.com (checked every few hours); this checks right now. */
function WebsitePricesButton({ onDone }: { onDone: () => void }) {
  const { run, busy } = useAction();
  const toast = useToast();
  const go = async () => {
    const r = await run(() => post<{ updated: number; added: number; kept: number }>('/api/sale-menu/website-prices'));
    if (!r) return;
    const parts = [r.updated ? `${r.updated} price${r.updated === 1 ? '' : 's'} changed` : null, r.added ? `${r.added} added` : null, r.kept ? `${r.kept} kept (you changed them here)` : null].filter(Boolean);
    toast(parts.length ? `Matched westcoastwirelessca.com: ${parts.join(', ')}.` : 'iPhone prices already match westcoastwirelessca.com.', 'good');
    onDone();
  };
  return <Button size="sm" variant="ghost" icon="refresh" busy={busy} onClick={go}>Match website prices</Button>;
}

/** A phone, tablet or laptop: pick the size, then add it (or a phone in stock with its IMEI). */
function ModelSheet({ model, onClose, onAddProduct, onAddDevice, onPriceSaved }: {
  model: Model;
  onClose: () => void;
  onAddProduct: (it: MenuItem, opts: { note?: string | null; priceCents?: number }) => void;
  onAddDevice: AddDevice;
  onPriceSaved: (id: string, price: number) => void;
}) {
  const { can, me } = useSession();
  const { run, busy } = useAction();
  const toast = useToast();
  const [checking, setChecking] = useState(false);
  const items = model.items;
  const [pickId, setPickId] = useState((items.find((i) => i.price_cents > 0) ?? items[0]).id);
  const [prices, setPrices] = useState<Record<string, number>>(() => Object.fromEntries(items.map((i) => [i.id, i.price_cents])));
  const item = items.find((i) => i.id === pickId) ?? items[0];
  const regular = prices[item.id] ?? item.price_cents;
  const [price, setPrice] = useState<number | null>(regular || null);
  const [saveRegular, setSaveRegular] = useState(false);
  const [color, setColor] = useState<string | null>(null);
  const [code, setCode] = useState('');
  useEffect(() => { setPrice(regular || null); setSaveRegular(!regular && can('inventory.edit')); }, [item.id, regular]); // eslint-disable-line react-hooks/exhaustive-deps

  const brand = item.brand;
  const stock = useLoad<any[]>(() => (brand ? get(`/api/devices${qs({ brand, limit: 300 })}`) : Promise.resolve([])), [brand]);
  const here = (stock.data ?? []).filter((d) => normModel(d.model) === normModel(model.model));
  const colors = item.details?.colors ?? [];
  const digits = code.replace(/\D/g, '');
  const imeiBad = digits.length === 15 && digits === code.trim() && !isValidImei(digits);
  const changed = price !== null && price !== regular;
  const canAdd = price !== null && price > 0 && !imeiBad;
  const title = `${brand && !model.model.startsWith(brand) ? `${brand} ` : ''}${model.model}`;

  const add = async () => {
    if (price === null) return;
    // The IMEI or serial of a phone in inventory sells that phone (it leaves inventory, with its
    // cost), with the same checks as scanning it. Not in inventory: a menu line with the IMEI on it.
    if (code.trim()) {
      setChecking(true);
      let found: any;
      try {
        found = (await get(`/api/lookup${qs({ code: code.trim() })}`)).result;
      } catch (e) {
        toast(e instanceof Error ? e.message : 'That IMEI couldn’t be checked. Try again.', 'error');
        return;
      } finally {
        setChecking(false);
      }
      if (found?.type === 'device') {
        if (onAddDevice(found.device) === true) toast(`That one is in stock: ${found.device.title} is in the sale at ${formatCents(found.device.price_cents)}.`, 'good');
        return;
      }
    }
    const note = [color, code.trim() ? (digits.length === 15 && digits === code.trim() ? `IMEI ${digits}` : `S/N ${code.trim()}`) : null].filter(Boolean).join(' · ') || null;
    if (changed && saveRegular && can('inventory.edit')) {
      const saved = await run(() => patch(`/api/products/${item.id}`, { priceCents: price }), { success: `${item.name} is now ${formatCents(price)}.` });
      if (!saved) return;
      setPrices({ ...prices, [item.id]: price });
      onPriceSaved(item.id, price);
      onAddProduct({ ...item, price_cents: price }, { note });
      return;
    }
    onAddProduct(item, { note, priceCents: changed ? price : undefined });
  };

  return (
    <Sheet title={title} onClose={onClose} wide footer={
      <>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" icon="bag" busy={busy || checking} disabled={!canAdd} onClick={add}>{price ? `Add to sale · ${formatCents(price)}` : 'Enter a price'}</Button>
      </>
    }>
      <div className="model-head">
        <span className="model-pic"><Picture item={{ ...item, model: model.model }} size={140} /></span>
        <div className="stack-sm grow">
          {item.details?.condition ? <div><Badge tone="blue">{item.details.condition}</Badge></div> : null}
          <div className="small muted">
            {item.price_source === 'wcw' ? 'Price from westcoastwirelessca.com.' : item.price_source === 'swappa' ? 'Price from recent online sales (used, Swappa average).' : item.price_source === 'retail' ? 'Online retail price.' : 'Your price.'}
            {can('inventory.edit') ? ' Change it below; tick the box to keep the new price.' : ''}
          </div>
        </div>
      </div>

      {items.length > 1 || item.variant ? (
        <div className="stack-sm">
          <span className="small strong">{items.some((i) => /·/.test(i.variant ?? '')) ? 'Configuration' : 'Storage'}</span>
          <div className="svc-grid">
            {items.map((i) => (
              <button key={i.id} type="button" className="svc" aria-pressed={i.id === item.id} onClick={() => setPickId(i.id)}>
                <span className="t">{i.variant ?? i.name}</span>
                <span className="p">{prices[i.id] ? formatCents(prices[i.id]) : <span className="ask">Set price</span>}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {colors.length ? (
        <div className="stack-sm">
          <span className="small strong">Color <span className="muted" style={{ fontWeight: 400 }}>optional</span></span>
          <div className="chips">
            {colors.map((c) => <button key={c} type="button" className="chip" aria-pressed={color === c} onClick={() => setColor(color === c ? null : c)}>{c}</button>)}
          </div>
        </div>
      ) : null}

      <div className="form-grid">
        <Field label="Price for this sale" hint={regular ? `regular ${formatCents(regular)}` : 'no price yet'}>
          <MoneyInput value={price} onChange={setPrice} ariaLabel="Price for this sale" />
        </Field>
        <Field label="IMEI or serial" hint={imeiBad ? 'That IMEI doesn’t add up. Dial *#06# to check.' : 'optional. A phone in stock with it is sold from stock'}>
          <input className="input" value={code} onChange={(e) => setCode(e.target.value)} aria-invalid={imeiBad} placeholder="*#06# shows it" autoComplete="off" />
        </Field>
      </div>
      {changed && can('inventory.edit') ? (
        <label className="check"><input type="checkbox" checked={saveRegular} onChange={(e) => setSaveRegular(e.target.checked)} /> Make {price ? formatCents(price) : 'this'} the regular price for {item.name}</label>
      ) : null}
      {changed && !can('sale.price_override') ? <div className="banner warn"><Icon.lock /><div className="small">A manager will be asked to approve this price at checkout.</div></div> : null}

      {here.length ? (
        <div className="stack-sm">
          <span className="small strong">In stock at {me?.location?.name ?? 'this store'} ({here.length})</span>
          <div className="list">
            {here.map((d: any) => (
              <button key={d.id} type="button" className="list-row" onClick={() => onAddDevice(d)}>
                <span className="thumb"><DevicePhoto url={d.image_url} brand={d.brand} model={d.model} color={d.color} category={d.category_id} size={48} /></span>
                <span className="main-col">
                  <span className="t" style={{ display: 'block' }}>{d.title}</span>
                  <span className="s" style={{ display: 'block' }}>{d.detail}{d.battery_health ? ` · Battery ${d.battery_health}%` : ''}</span>
                  {d.hold_until && d.hold_until > Date.now() ? <span className="s" style={{ display: 'block', color: 'var(--amber)' }}>On hold until {formatDateTime(d.hold_until)}</span> : null}
                </span>
                <span className="strong num">{formatCents(d.price_cents)}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </Sheet>
  );
}

/** One item with no price yet, or a price change from the menu. */
function PriceSheet({ item, mode, onClose, onSaved, onAdd }: {
  item: MenuItem;
  mode: 'sell' | 'edit';
  onClose: () => void;
  onSaved: (price: number) => void;
  onAdd: (it: MenuItem, opts: { note?: string | null; priceCents?: number }) => void;
}) {
  const { can } = useSession();
  const { run, busy } = useAction();
  const [price, setPrice] = useState<number | null>(item.price_cents || null);
  const [save, setSave] = useState(mode === 'edit' || (!item.price_cents && can('inventory.edit')));
  const go = async () => {
    if (price === null || price <= 0) return;
    if (save && can('inventory.edit') && price !== item.price_cents) {
      const ok = await run(() => patch(`/api/products/${item.id}`, { priceCents: price }), { success: `${item.name} is now ${formatCents(price)}.` });
      if (!ok) return;
      onSaved(price);
      if (mode === 'edit') return onClose();
      return onAdd({ ...item, price_cents: price }, {});
    }
    if (mode === 'edit') return onClose();
    onAdd(item, { priceCents: price });
  };
  return (
    <Sheet title={mode === 'edit' ? 'Change the price' : 'Set the price'} onClose={onClose} footer={
      <>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" busy={busy} disabled={price === null || price <= 0} onClick={go}>{mode === 'edit' ? 'Save price' : `Add to sale${price ? ` · ${formatCents(price)}` : ''}`}</Button>
      </>
    }>
      <div className="model-head">
        <span className="model-pic"><Picture item={item} size={110} /></span>
        <div className="stack-sm grow">
          <div className="strong">{item.name}</div>
          <div className="small muted">{item.price_cents ? `Regular price ${formatCents(item.price_cents)}` : 'No price yet.'}</div>
        </div>
      </div>
      <Field label={mode === 'edit' ? 'New regular price' : 'Price'}><MoneyInput value={price} onChange={setPrice} autoFocus ariaLabel="Price" /></Field>
      {mode === 'sell' && can('inventory.edit') ? <label className="check"><input type="checkbox" checked={save} onChange={(e) => setSave(e.target.checked)} /> Keep this as the regular price</label> : null}
      {mode === 'sell' && !can('sale.price_override') ? <div className="banner warn"><Icon.lock /><div className="small">A manager will be asked to approve this price at checkout.</div></div> : null}
    </Sheet>
  );
}

/**
 * Something that isn't on the menu yet: add it to inventory right from the sale (name, price,
 * cost, category, how many you have, barcode) and put it in the sale. Phones go in Inventory
 * with their IMEI.
 */
function QuickAddSheet({ name, onClose, onAdded }: { name: string; onClose: () => void; onAdded: (p: MenuItem) => void }) {
  const { can } = useSession();
  const cats = useLoad<{ id: string; name: string; tracking: string; parent_id?: string | null }[]>(() => get('/api/categories'), []);
  const { run, busy } = useAction();
  const [f, setF] = useState({ name: name.trim().replace(/^./, (c) => c.toUpperCase()), price: null as number | null, cost: null as number | null, categoryId: '', qty: '1', barcode: '' });
  const qtyCats = (cats.data ?? []).filter((c) => c.tracking === 'quantity');
  useEffect(() => {
    if (f.categoryId || !qtyCats.length) return;
    const pick = qtyCats.find((c) => /accessor/i.test(c.name)) ?? qtyCats[0];
    setF((x) => ({ ...x, categoryId: pick.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cats.data]);
  // Its picture comes up by itself from the name ("Charger Apple Type C": a white USB-C charger).
  const picName = useDebounced(f.name.trim(), 400);
  const pic = useLoad<{ url: string | null }>(() => (picName.length >= 3 ? get(`/api/photos/suggest${qs({ name: picName, category: f.categoryId })}`) : Promise.resolve({ url: null })), [picName, f.categoryId]);
  const qty = Number(f.qty);
  const ok = f.name.trim().length >= 2 && f.price !== null && f.price > 0 && !!f.categoryId && Number.isInteger(qty) && qty >= 0 && qty <= 9999;
  const save = async () => {
    const p = await run(() => post('/api/products', {
      name: f.name.trim(), categoryId: f.categoryId, priceCents: f.price, costCents: f.cost ?? undefined, barcode: f.barcode.trim() || null, initialQty: qty, imageUrl: pic.data?.url ?? null,
    }), { success: `${f.name.trim()} is in inventory and in the sale.` });
    if (p) onAdded(p as MenuItem);
  };
  return (
    <Sheet
      title="Add a new item"
      onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" icon="plus" busy={busy} disabled={!ok} onClick={save}>Add to inventory and the sale</Button></>}
    >
      <div className="form-grid" data-enter="off" onKeyDown={enterToNextBox}>
        <div className="row full" style={{ gap: 12 }}><span className="thumb" style={{ width: 64, height: 64 }}><ProductPicture url={pic.data?.url ?? null} categoryId={f.categoryId} name={f.name || 'Item'} size={64} /></span><span className="small muted">{pic.data?.url ? 'Its picture, picked by itself from the name.' : 'Its picture comes up by itself from the name.'}</span></div>
        <Field label="Name" className="full"><input className="input input-lg" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} aria-label="Item name" /></Field>
        <Field label="Price"><MoneyInput value={f.price} onChange={(v) => setF({ ...f, price: v })} /></Field>
        {/* Costs are the owner's: only the owner types one (the server ignores anyone else's). */}
        {can('inventory.view_cost') ? <Field label="Cost" hint="optional, for your profit reports"><MoneyInput value={f.cost} onChange={(v) => setF({ ...f, cost: v })} /></Field> : null}
        <Field label="Category">
          <select className="select" value={f.categoryId} onChange={(e) => setF({ ...f, categoryId: e.target.value })} aria-label="Category">
            {qtyCats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="How many you have" hint="counting the one you're selling"><input className="input" inputMode="numeric" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value.replace(/\D/g, '') })} aria-label="How many you have" /></Field>
        <Field label="Barcode" hint="optional: scan it so it rings up next time" className="full"><input className="input" value={f.barcode} onChange={(e) => setF({ ...f, barcode: e.target.value })} aria-label="Barcode" /></Field>
      </div>
      <p className="small muted" style={{ margin: 0 }}>It’s saved in Inventory, so it shows up on the menu and in search from now on. Phones are added in Inventory with their IMEI.</p>
    </Sheet>
  );
}
